<template>
    <div class="lost-and-found-container">
        <div class="header-section">
            <div class="header-content">
                <a-avatar shape="square" class="header-avatar" :size="80" src="/images/resultsis.png" />
                <div class="header-text">
                    <h1 class="main-title">Lost & Found Search</h1>
                    <p class="font-web mt-0 mb-0 subtitle">
                        Search for your lost items or report found items across campus.
                    </p>
                    <p class="font-web mt-1 mb-0 subtitle-instruction" style="font-size: 0.95rem; color: #4b5563;">
                        If you locate your item, please note its Reference ID (<span style="color: #502b80; font-weight: 600;">LFXXXX_XXXX</span>) to contact staff and claim it at the designated <strong>&ldquo;Pick up at&rdquo;</strong> location.
                    </p>
                </div>
            </div>
            <!--  
                <div class="top-filters">
                    <button class="top-filter-btn active">
                    <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
                    Home
                    </button>
                    <button class="top-filter-btn">
                    <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
                    Items
                    </button>
                </div>
            -->
        </div>

        <div class="search-section">
            <div class="search-input-wrapper">
                <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                <input type="text" class="search-input" placeholder="Search for lost or found items..."
                    v-model="searchQuery" />
            </div>
        </div>

        <div class="filter-section">
            <!-- Item Types -->
            <div class="filter-group">
                <button v-for="item in item_types" :key="'type-' + item.id" class="filter-pill"
                    :class="{ active: selectedItemTypes.includes(item.id) }" @click="toggleItemType(item.id)">
                    <span class="pill-icon" v-html="getTypeIcon(item.id)"></span>
                    {{ item.type_name }}
                </button>
            </div>

            <!-- Buildings -->
            <div class="filter-group">
                <button v-for="building in buildings" :key="'building-' + building.code" class="filter-pill"
                    :class="{ active: selectedBuilding === building.code }"
                    @click="selectedBuilding = selectedBuilding === building.code ? null : building.code">
                    <span class="pill-icon" v-html="getBuildingIcon(building.code)"></span>
                    {{ building.name }}
                </button>
            </div>
        </div>

        <!-- Items Grid -->
        <div class="items-grid-section">
            <!-- Top Bar: Filters & Pagination -->
            <div class="items-header-bar"
                style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 1rem;">
                <!-- Active Filters -->
                <div class="active-filters" style="display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center;">
                    <span v-if="searchQuery || selectedItemTypes.length > 0 || selectedBuilding"
                        style="font-size: 0.9rem; color: #6b7280; font-weight: 500;">
                        Active Filters:
                    </span>

                    <a-tag v-if="searchQuery" closable @close="searchQuery = ''">
                        Search: {{ searchQuery }}
                    </a-tag>

                    <a-tag v-for="typeId in selectedItemTypes" :key="'active-type-' + typeId" closable
                        @close="toggleItemType(typeId)">
                        Type: {{ getItemTypeName(typeId) }}
                    </a-tag>

                    <a-tag v-if="selectedBuilding" closable @close="selectedBuilding = null">
                        Building: {{ getActiveBuildingName }}
                    </a-tag>

                    <a-button v-if="searchQuery || selectedItemTypes.length > 0 || selectedBuilding" type="link"
                        size="small" @click="clearFilters" style="padding: 0; margin-left: 0.5rem;">
                        Clear All
                    </a-button>
                </div>

                <!-- Top Pagination -->
                <div class="pagination-wrapper" v-if="totalPages > 1">
                    <a-pagination v-model="currentPage" :total="totalItems" :pageSize="itemsPerPage" show-less-items />
                </div>
            </div>

            <div class="items-grid" v-if="isLoading">
                <ItemCard v-for="n in itemsPerPage" :key="'skeleton-' + n" :loading="true" :item="{}" />
            </div>

            <div class="items-grid" v-else-if="items.length > 0">
                <ItemCard v-for="item in paginatedItems" :key="item.id" :item="item" />
            </div>

            <div v-else style="padding: 4rem 0; display: flex; justify-content: center; width: 100%;">
                <a-empty description="No items found in the system, or your search criteria did not match any items." />
            </div>

            <!-- Pagination -->
            <div class="pagination-wrapper" v-if="totalPages > 1" style="display: flex; justify-content: flex-end;">
                <a-pagination v-model="currentPage" :total="totalItems" :pageSize="itemsPerPage" show-less-items />
            </div>
        </div>
    </div>
</template>

<script>
import './lostandfound.css';
import ItemCard from './ItemCard.vue';

export default {
    name: 'MainLostAndFound',
    components: {
        ItemCard
    },
    methods: {
        getTypeIcon(id) {
            if (id === 1) return '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>';
            if (id === 2) return '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>';
            if (id === 3) return '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"></path></svg>';
            return '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>';
        },
        getBuildingIcon(code) {
            return '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>';
        },
        async fetchOptions() {
            try {
                const response = await this.$http.get('/lost-found/options/');
                if (response.status === 200) {
                    const result = response.data;
                    if (result.success && result.data) {
                        this.item_types = result.data.item_types || [];
                        this.item_statuses = result.data.item_statuses || [];
                        this.buildings = result.data.buildings || [];
                    }
                }
            } catch (error) {
                // Handle error
            }
        },
        getItemTypeName(id) {
            const type = this.item_types.find(t => t.id === id);
            return type ? type.type_name : '';
        },
        toggleItemType(id) {
            const index = this.selectedItemTypes.indexOf(id);
            if (index > -1) {
                this.selectedItemTypes.splice(index, 1);
            } else {
                this.selectedItemTypes.push(id);
            }
        },
        clearFilters() {
            this.searchQuery = '';
            this.selectedItemTypes = [];
            this.selectedBuilding = null;
            this.currentPage = 1;
        },
        async fetchItems() {
            this.isLoading = true;
            try {
                let url = `/lost-found/items/?status=1&page=${this.currentPage}`;
                if (this.searchQuery) url += `&search=${encodeURIComponent(this.searchQuery)}`;
                if (this.selectedItemTypes.length > 0) url += `&type=${this.selectedItemTypes.join(',')}`;
                if (this.selectedBuilding) url += `&building=${this.selectedBuilding}`;

                const response = await this.$http.get(url);
                if (response.status === 200) {
                    const result = response.data;
                    this.totalItems = result.count || 0;

                    const rawItems = result.results || [];
                    this.items = rawItems.map(item => ({
                        id: item.id,
                        lf_id: item.ref,
                        item_name: item.item_name,
                        item_type: item.item_type_detail ? item.item_type_detail.type_name : '',
                        item_detail: item.item_detail,
                        item_find: item.item_find_detail ? `${item.item_find_detail.building_display} ${item.item_find_detail.room_name || item.item_find_detail.room_number || ''}`.trim() : '',
                        item_position: item.item_position_detail ? `${item.item_position_detail.building_display} ${item.item_position_detail.room_name || item.item_position_detail.room_number || ''}`.trim() : '',
                        status: item.item_status_detail ? item.item_status_detail.status_name : '',
                        image: (item.photos && item.photos.length > 0) ? item.photos[0].image_url : null
                    }));
                }
            } catch (error) {
                // Handle error
            } finally {
                this.isLoading = false;
            }
        }
    },
    watch: {
        currentPage() {
            this.fetchItems();
        },
        searchQuery() {
            this.currentPage = 1;
            this.fetchItems();
        },
        selectedItemTypes: {
            handler() {
                this.currentPage = 1;
                this.fetchItems();
            },
            deep: true
        },
        selectedBuilding() {
            this.currentPage = 1;
            this.fetchItems();
        }
    },
    data() {
        return {
            isLoading: false,
            searchQuery: '',
            selectedItemTypes: [],
            selectedBuilding: null,
            currentPage: 1,
            itemsPerPage: 12,
            item_types: [],
            item_statuses: [],
            buildings: [],
            items: [],
            totalItems: 0
        }
    },
    computed: {
        paginatedItems() {
            return this.items;
        },
        totalPages() {
            return Math.ceil(this.totalItems / this.itemsPerPage);
        },
        getActiveBuildingName() {
            if (!this.selectedBuilding) return '';
            const building = this.buildings.find(b => b.code === this.selectedBuilding);
            return building ? building.name : '';
        }
    },
    mounted() {
        this.fetchOptions();
        this.fetchItems();
    }
}
</script>
